/**
 * @author       matt@zumper.com (Matthieu Devlin)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Router middleware for dispatching traktor events
 */

import type { PayloadAction } from '@reduxjs/toolkit'
import { IS_SERVER } from '@zcode/env'
import type { ThunkMiddleware } from 'redux-thunk'
import type { TrackEventPayload, User } from './actions'
import { EVENT } from './constants'
import type { LocalStorage } from './utils/getTrackingTokens'
import { type InjectedHelpers, trackEvent } from './utils/track'

export const createTraktorMiddleware = <
  State extends object,
  LS extends LocalStorage,
  ExtraThunkArg,
>({
  selectIsBot,
  siteActions,
  trackCallback = trackEvent,
  trackOptimizelyTraktorEvents,
  ...injectedHelpers
}: InjectedHelpers<LS> & {
  selectIsBot: (state: State) => boolean
  siteActions?:
    | Partial<
        Record<string, (action: PayloadAction<unknown>, state: State) => void>
      >
    | undefined
  trackCallback?:
    | ((
        name: string,
        props: object | undefined,
        user: User | undefined,
        filters: object | undefined,
        location: object | undefined,
        injectedHelpers: InjectedHelpers<LS>
      ) => void)
    | undefined
  trackOptimizelyTraktorEvents?:
    | ((
        name: string,
        props: object | undefined
      ) => (
        _: unknown,
        getState: () => State,
        thunkExtraArg: ExtraThunkArg
      ) => void)
    | undefined
}) => {
  const thunkMiddleware: ThunkMiddleware<
    State,
    { readonly type: string },
    ExtraThunkArg
  > = (store) => (next) => (action: PayloadAction<unknown>) => {
    // call all other middleware and reducers before emitting traktor events so that we can have
    // the state of the application after any caught actions have been reduced
    const result = next(action)
    const state = store.getState()
    const isBot = selectIsBot(state)

    // skip over empty actions https://rollbar.com/zumper/npr/items/112073/
    if (IS_SERVER || isBot || !action) {
      return result
    }

    const { type } = action

    siteActions?.[type]?.(action, state)

    switch (type) {
      case EVENT: {
        const { payload } = action
        const {
          name,
          props,
          user,
          filters,
          location,
          sendToOptimizely = true,
        } = (payload || {}) as TrackEventPayload
        trackCallback(name, props, user, filters, location, injectedHelpers)
        if (sendToOptimizely && trackOptimizelyTraktorEvents) {
          store.dispatch(trackOptimizelyTraktorEvents(name, props))
        }
        return result
      }
      default: {
        return result
      }
    }
  }
  return thunkMiddleware
}
